import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Tag, Clock, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { readingTime, isHtml, relatedArticles } from "@/lib/articleUtils";
import { useLanguage } from "@/i18n/LanguageContext";
import { bi } from "@/lib/bilingual";
import { useCachedGet } from "@/hooks/useCachedGet";
import PageSkeleton from "@/components/site/PageSkeleton";
import SEO from "@/components/site/SEO";

const ArticleDetail = () => {
  const { slug } = useParams();
  const { t, locale, lang } = useLanguage();
  const { data: article, ready } = useCachedGet(`/articles/${slug}`);
  const { data: allRaw } = useCachedGet("/articles", { published: true });
  const all = Array.isArray(allRaw) ? allRaw : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!ready) return <PageSkeleton />;
  if (article === false) return <div className="pt-40 text-center text-zinc-500">{t("articles.notFound")}</div>;
  if (!article) return <PageSkeleton />;

  const title = bi(article.title, lang);
  const excerpt = bi(article.excerpt, lang);
  const content = bi(article.content, lang);
  const category = bi(article.category, lang);
  const seoTitle = bi(article.seo_title, lang) || title;
  const seoDesc = bi(article.seo_description, lang) || excerpt;

  const related = relatedArticles(all, article, 3);
  const mins = readingTime(content);
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(title);

  const breadcrumbs = [
    { name: t("nav.home"), url: (typeof window !== "undefined" ? window.location.origin : "") + "/" },
    { name: t("nav.articles"), url: (typeof window !== "undefined" ? window.location.origin : "") + "/artikel" },
    { name: title, url: currentUrl },
  ];

  return (
    <article>
      <SEO
        title={seoTitle}
        description={seoDesc}
        image={article.thumbnail}
        type="article"
        article={{ ...article, title, excerpt, content, category }}
        breadcrumbs={breadcrumbs}
      />
      <section className="pt-12 pb-8 bg-[#fff5f5]">
        <div className="container-wide">
          <Link to="/artikel" data-testid="back-to-articles" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-maroon-800">
            <ArrowLeft size={14} /> {t("articles.back")}
          </Link>
          <div className="mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-maroon-100 text-maroon-800 font-semibold">{category}</span>
              <span className="flex items-center gap-1 text-zinc-500"><Calendar size={12} /> {new Date(article.created_at).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })}</span>
              <span className="flex items-center gap-1 text-zinc-500"><User size={12} /> {article.author}</span>
              <span className="flex items-center gap-1 text-zinc-500"><Clock size={12} /> {t("common.readMinutes", { n: mins })}</span>
            </div>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-display font-black text-zinc-900 tracking-tighter text-balance leading-[1.1]">
              {title}
            </h1>
            {excerpt && <p className="mt-6 text-lg text-zinc-600 leading-relaxed">{excerpt}</p>}
          </div>
        </div>
      </section>

      {article.thumbnail && (
        <section className="pb-12 bg-[#fff5f5]">
          <div className="container-wide">
            <div className="aspect-[16/8] rounded-3xl overflow-hidden max-w-5xl mx-auto">
              <img src={resolveMediaUrl(article.thumbnail)} alt={title} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      <section className="pb-16 bg-white">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto">
            {isHtml(content) ? (
              <div className="prose prose-zinc max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-maroon-700 prose-img:rounded-xl prose-p:text-zinc-700 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              <div className="prose-mandala">
                {(content || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            )}

            {article.tags?.length > 0 && (
              <div className="mt-12 pt-8 border-t border-zinc-200 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-100 text-zinc-700 text-xs">
                    <Tag size={10} /> {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 flex items-center gap-3">
              <span className="text-sm font-semibold text-zinc-700 flex items-center gap-2"><Share2 size={14} /> {t("common.share")}</span>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-maroon-800 hover:text-white hover:border-maroon-800 transition"><Twitter size={14} /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-maroon-800 hover:text-white hover:border-maroon-800 transition"><Facebook size={14} /></a>
              <a href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(currentUrl)}&title=${shareText}`} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-maroon-800 hover:text-white hover:border-maroon-800 transition"><Linkedin size={14} /></a>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-pad bg-zinc-50">
          <div className="container-wide">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-zinc-900 tracking-tight">{t("articles.related")}</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link key={a.id} to={`/artikel/${a.slug}`} className="group block">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-zinc-100">
                    {a.thumbnail && <img src={resolveMediaUrl(a.thumbnail)} alt={bi(a.title, lang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="pt-4">
                    <span className="text-xs uppercase tracking-widest text-maroon-700 font-bold">{bi(a.category, lang)}</span>
                    <h3 className="mt-2 text-lg font-display font-bold text-zinc-900 group-hover:text-maroon-800 line-clamp-2">{bi(a.title, lang)}</h3>
                    <p className="mt-1 text-xs text-zinc-500 flex items-center gap-1"><Clock size={11} /> {t("common.readMinutes", { n: readingTime(bi(a.content, lang)) })}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default ArticleDetail;
